import snakeCase from 'lodash/snakeCase';
import Assignees from '@/constants/Assignees';
import ConstituentSearchFilterField from '@/constants/ConstituentSearchFilterField';
import DateType from '@/constants/DateType';
import FilterOperator from '@/constants/forms/FilterOperator';
import LabelResourceTypes from '@/constants/labels/LabelResourceTypes';
import type AdvancedConstituentSearchFilter from '@/types/AdvancedConstituentSearchFilter';
import type CustomField from '@/types/api/CustomField';
import { formatDateToString } from '@/utils/dates';
import DateTypeOptions from '../constants/DateTypeOptions';
import type ConstituentSearchFilterParams from '../types/ConstituentSearchFilterParams';

// TODO(jon): Once the old search is fully deprecated, the ConsituentSearchFilterParams
// type is going to be updated to only include filters and advancedFilters, so this
// will need to be updated accordingly. At that time we can also go in and
// enum-ify the loose strings that are in the conditionals below.
const mapOldSavedFilters = (
  searchParams: Omit<
    ConstituentSearchFilterParams,
    'filters' | 'advancedFilters'
  >,
  customFields: CustomField[],
) => {
  const result: AdvancedConstituentSearchFilter[] = [];

  const {
    // these fields are not being used by advanced search, so we are pulling them out
    donationAgg,

    // these fields need special attention
    constituentCategories,
    constituentRules,
    dateType,
    labelIds,
    labelResourceType,
    startDate,
    endDate,
    userIds,

    // everything else
    ...rest
  } = searchParams;

  if (userIds) {
    switch (userIds) {
      case Assignees.ALL:
        break;
      case Assignees.ASSIGNED:
        result.push({
          field: ConstituentSearchFilterField.USER_IDS,
          operator: FilterOperator.IS_NOT_NULL,
        });
        break;
      case Assignees.UNASSIGNED:
        result.push({
          field: ConstituentSearchFilterField.USER_IDS,
          operator: FilterOperator.IS_NULL,
        });
        break;
      default:
        result.push({
          field: ConstituentSearchFilterField.USER_IDS,
          operator: FilterOperator.ANY,
          value: [userIds],
        });
        break;
    }
  }

  const categoriesAndRules = [
    ...(constituentCategories || []),
    ...(constituentRules || []),
  ];
  if (categoriesAndRules.length) {
    result.push({
      field: ConstituentSearchFilterField.CONSTITUENT_CATEGORIES,
      operator: FilterOperator.ANY,
      value: categoriesAndRules,
    });
  }

  if (dateType) {
    if (dateType === DateType.CUSTOM_DATES) {
      if (startDate) {
        result.push({
          field: ConstituentSearchFilterField.DONATION_DATE,
          operator: FilterOperator.GREATER_THAN,
          value: formatDateToString(startDate),
        });
      }

      if (endDate) {
        result.push({
          field: ConstituentSearchFilterField.DONATION_DATE,
          operator: FilterOperator.LESS_THAN,
          value: formatDateToString(endDate),
        });
      }
    } else if (DateTypeOptions.map(({ value }) => value).includes(dateType)) {
      result.push({
        field: ConstituentSearchFilterField.DONATION_DATE_TYPE,
        operator: FilterOperator.IS,
        value: dateType,
      });
    }
  }

  if (Array.isArray(labelIds) && labelIds.length) {
    result.push({
      field:
        labelResourceType === LabelResourceTypes.DON
          ? ConstituentSearchFilterField.DONATION_LABEL_IDS
          : ConstituentSearchFilterField.CONSTITUENT_LABEL_IDS,
      operator: FilterOperator.ANY,
      value: labelIds,
    });
  }

  Object.entries(rest).forEach(([key, val]) => {
    // Omit null values
    if (val === null) return;

    let field = snakeCase(key);
    let value = val;
    let operator = FilterOperator.IS;

    const customFieldId = customFields.find(
      ({ slug }) => snakeCase(slug) === field,
    )?.customFieldId;

    if (customFieldId) {
      field = customFieldId;
    }

    if (key === 'nameEmailPhone') {
      operator = FilterOperator.CONTAINS;
    }

    if (value instanceof Date) {
      value = formatDateToString(value);
    }

    if (Array.isArray(value)) {
      if (!value.length) return;
      operator = FilterOperator.ANY;
    }

    if (['bornAfterDate', 'bornBeforeDate'].includes(key)) {
      field = ConstituentSearchFilterField.DOB;
    }

    if (['minDonation', 'maxDonation'].includes(key)) {
      field = ConstituentSearchFilterField.DONATION_AMOUNT;
    }

    if (['minMinutes', 'maxMinutes'].includes(key)) {
      field = ConstituentSearchFilterField.DONATION_MINUTES;
    }

    if (['minDonation', 'minMinutes', 'bornAfterDate'].includes(key)) {
      operator = FilterOperator.GREATER_THAN;
    }

    if (['maxDonation', 'maxMinutes', 'bornBeforeDate'].includes(key)) {
      operator = FilterOperator.LESS_THAN;
    }

    const item: AdvancedConstituentSearchFilter = { field, operator, value };

    if (customFieldId) {
      item.isCustomField = true;
    }

    result.push(item);
  });

  return result;
};

export default mapOldSavedFilters;
